import React, { useState, useEffect, useReducer, useContext, useMemo } from "react";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const TICKET_LIST_SYNC_EVENT = "ticket-list-sync";

const useStyles = makeStyles((theme) => ({
    ticketsListWrapper: {
        position: "relative",
        display: "flex",
        height: "100%",
        flexDirection: "column",
        overflow: "hidden",
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
    },

    ticketsList: {
        flex: 1,
        maxHeight: "100%",
        overflowY: "scroll",
        ...theme.scrollbarStyles,
        borderTop: "2px solid rgba(0, 0, 0, 0.12)",
    },

    ticketsListHeader: {
        color: "rgb(67, 83, 105)",
        zIndex: 2,
        backgroundColor: "white",
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },

    ticketsCount: {
        fontWeight: "normal",
        color: "rgb(104, 121, 146)",
        marginLeft: "8px",
        fontSize: "14px",
    },

    noTicketsText: {
        textAlign: "center",
        color: "rgb(104, 121, 146)",
        fontSize: "14px",
        lineHeight: "1.4",
    },

    noTicketsTitle: {
        textAlign: "center",
        fontSize: "16px",
        fontWeight: "600",
        margin: "0px",
    },

    noTicketsDiv: {
        display: "flex",
        // height: "190px",
        margin: 40,
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
    },
}));

const ticketSortAsc = (a, b) => {
    const left = a.lastMessageAt || a.updatedAt;
    const right = b.lastMessageAt || b.updatedAt;
    if (left < right) {
        return -1;
    }
    if (left > right) {
        return 1;
    }
    return 0;
}

const ticketSortDesc = (a, b) => {
    const left = a.lastMessageAt || a.updatedAt;
    const right = b.lastMessageAt || b.updatedAt;
    if (left > right) {
        return -1;
    }
    if (left < right) {
        return 1;
    }
    return 0;
}

const reducer = (state, action) => {
    //console.log("action", action, state)
    const sortDir = action.sortDir;

    if (action.type === "LOAD_TICKETS") {
        const newTickets = action.payload;

        newTickets.forEach((ticket) => {
            const ticketIndex = state.findIndex((t) => t.id === ticket.id);
            if (ticketIndex !== -1) {
                state[ticketIndex] = ticket;
                if (ticket.unreadMessages > 0) {
                    state.unshift(state.splice(ticketIndex, 1)[0]);
                }
            } else {
                state.push(ticket);
            }
        });
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "REPLACE_TICKETS") {
        const newTickets = action.payload;
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? newTickets.sort(ticketSortAsc) : newTickets.sort(ticketSortDesc);
        }
        return [...newTickets];
    }

    if (action.type === "RESET_UNREAD") {
        const ticketId = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state[ticketIndex].unreadMessages = 0;
        }

        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET") {
        const ticket = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            state[ticketIndex] = ticket;
        } else {
            state.unshift(ticket);
        }
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
        const ticket = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            state[ticketIndex] = ticket;
            state.unshift(state.splice(ticketIndex, 1)[0]);
        } else {
            if (action.status === action.payload.status) {
                state.unshift(ticket);
            }
        }
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET_CONTACT") {
        const contact = action.payload;
        const ticketIndex = state.findIndex((t) => t.contactId === contact.id);
        if (ticketIndex !== -1) {
            state[ticketIndex].contact = contact;
        }
        return [...state];
    }

    if (action.type === "DELETE_TICKET") {
        const ticketId = action.payload;
        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state.splice(ticketIndex, 1);
        }

        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const buildCoachPendingMap = (notes = []) => {
    return notes.reduce((acc, note) => {
        const ticketId = Number(note?.ticket?.id || 0);
        const ticketUuid = note?.ticket?.uuid ? String(note.ticket.uuid) : null;

        if (ticketId) {
            const current = acc[ticketId] || { count: 0, latestNote: null };
            acc[ticketId] = {
                count: current.count + 1,
                latestNote: current.latestNote || note
            };
        }

        if (ticketUuid) {
            const current = acc[ticketUuid] || { count: 0, latestNote: null };
            acc[ticketUuid] = {
                count: current.count + 1,
                latestNote: current.latestNote || note
            };
        }

        return acc;
    }, {});
};

const TicketsListCustom = (props) => {
    const {
        setTabOpen,
        status,
        searchParam,
        searchOnMessages,
        tags,
        users,
        showAll,
        selectedQueueIds,
        updateCount,
        style,
        whatsappIds,
        forceSearch,
        statusFilter,
        userFilter,
        sortTickets,
        unreplied
    } = props;

    const classes = useStyles();
    const [pageNumber, setPageNumber] = useState(1);
    let [ticketsList, dispatch] = useReducer(reducer, []);
    //   const socketManager = useContext(SocketContext);
    const { user, socket } = useContext(AuthContext);

    const { profile, queues } = user;
    const showTicketWithoutQueue = user.allTicket === 'enable' || user.allTicket === 'enabled';
    const companyId = user.companyId;

    // Derive visibility: style is undefined when visible, {width:0, height:0} when hidden
    const isVisible = !style || (style.width !== 0 && style.height !== 0);
    const [wasVisible, setWasVisible] = useState(isVisible);
    const [localForceRefresh, setLocalForceRefresh] = useState(0);
    const [coachPendingByTicket, setCoachPendingByTicket] = useState({});

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [status, searchParam, dispatch, showAll, tags, users, forceSearch, selectedQueueIds, whatsappIds, statusFilter, sortTickets, searchOnMessages, unreplied]);

    // Force re-fetch when tab becomes visible (handles stale data after status changes)
    useEffect(() => {
        if (isVisible && !wasVisible) {
            dispatch({ type: "RESET" });
            setPageNumber(1);
            setLocalForceRefresh(prev => prev + 1);
        }
        setWasVisible(isVisible);
    }, [isVisible]);

    const { tickets, hasMore, loading } = useTickets({
        pageNumber,
        searchParam,
        status,
        showAll,
        searchOnMessages: searchOnMessages ? "true" : "false",
        tags: JSON.stringify(tags),
        users: JSON.stringify(users),
        queueIds: JSON.stringify(selectedQueueIds),
        whatsappIds: JSON.stringify(whatsappIds),
        statusFilter: JSON.stringify(statusFilter),
        userFilter,
        sortTickets,
        forceSearch: `${forceSearch}_${localForceRefresh}`,
        unreplied: unreplied ? "true" : "false"
    });

    const refreshCoachPending = useMemo(() => {
        let mounted = true;

        const fetchCoachPending = async () => {
            try {
                const { data } = await api.get("/message-coach-notes/me");

                if (!mounted) {
                    return;
                }

                setCoachPendingByTicket(buildCoachPendingMap(Array.isArray(data) ? data : []));
            } catch (error) {
                if (mounted) {
                    setCoachPendingByTicket({});
                }
            }
        };

        fetchCoachPending.isMounted = () => mounted;
        fetchCoachPending.dispose = () => {
            mounted = false;
        };

        return fetchCoachPending;
    }, [companyId, user?.id]);

    useEffect(() => {
        refreshCoachPending();

        const interval = setInterval(() => {
            refreshCoachPending();
        }, 20000);

        return () => {
            clearInterval(interval);
            if (typeof refreshCoachPending.dispose === "function") {
                refreshCoachPending.dispose();
            }
        };
    }, [refreshCoachPending]);


    useEffect(() => {
        // const queueIds = queues.map((q) => q.id);
        // const filteredTickets = tickets.filter(
        //     (t) => queueIds.indexOf(t.queueId) > -1
        // );
        // const allticket = user.allTicket === 'enabled';
        // if (profile === "admin" || allTicket || allowGroup || allHistoric) {
        if (companyId) {
            dispatch({
                type: pageNumber === 1 ? "REPLACE_TICKETS" : "LOAD_TICKETS",
                payload: tickets,
                status,
                sortDir: sortTickets
            });
        }
        // } else {
        //  dispatch({ type: "LOAD_TICKETS", payload: filteredTickets });
        // }

    }, [tickets]);

    useEffect(() => {
        if (status !== "pending") {
            return undefined;
        }

        // Respaldo ante una perdida puntual de Socket.IO: la pestaña Esperando
        // se reconcilia sin exigir que el operador cambie de menú.
        const refreshInterval = window.setInterval(() => {
            setLocalForceRefresh(prev => prev + 1);
        }, 15000);

        return () => window.clearInterval(refreshInterval);
    }, [status]);

    useEffect(() => {
        const canViewAllQueues = showAll;

        const shouldUpdateTicket = ticket => {
            const ticketBelongsToVisibleQueue = canViewAllQueues
                || ((!ticket?.queueId && showTicketWithoutQueue) || selectedQueueIds.indexOf(ticket?.queueId) > -1);

            return (!ticket?.userId || ticket?.userId === user?.id || showAll) &&
                ticketBelongsToVisibleQueue
            // (!blockNonDefaultConnections || (ticket.status == 'group' && ignoreUserConnectionForGroups) || !user?.whatsappId || ticket.whatsappId == user?.whatsappId);
        }
        // const shouldUpdateTicketUser = (ticket) =>
        //     selectedQueueIds.indexOf(ticket?.queueId) > -1 && (ticket?.userId === user?.id || !ticket?.userId);

        const notBelongsToUserQueues = (ticket) =>
            !canViewAllQueues && ticket.queueId && selectedQueueIds.indexOf(ticket.queueId) === -1;

        const onCompanyTicketTicketsList = (data) => {
            // console.log("onCompanyTicketTicketsList", data)
            if (data.action === "updateUnread") {
                dispatch({
                    type: "RESET_UNREAD",
                    payload: data.ticketId,
                    status: status,
                    sortDir: sortTickets
                });
            }
            // console.log(shouldUpdateTicket(data.ticket))
            if (data.action === "update" &&
                shouldUpdateTicket(data.ticket) && data.ticket.status === status) {
                dispatch({
                    type: "UPDATE_TICKET",
                    payload: data.ticket,
                    status: status,
                    sortDir: sortTickets
                });
            }

            // A ticket can be reactivated by an incoming message after it was
            // closed. Re-fetch the visible tab even when its queue changed so
            // it appears immediately in "Esperando".
            if (data.action === "update" && data.ticket?.status === status) {
                setLocalForceRefresh(prev => prev + 1);
            }

            // else if (data.action === "update" && shouldUpdateTicketUser(data.ticket) && data.ticket.status === status) {
            //     dispatch({
            //         type: "UPDATE_TICKET",
            //         payload: data.ticket,
            //     });
            // }
            if (data.action === "update" && notBelongsToUserQueues(data.ticket)) {
                dispatch({
                    type: "DELETE_TICKET", payload: data.ticket?.id, status: status,
                    sortDir: sortTickets
                });
            }

            if (data.action === "update" && data.ticket.id && data.ticket.status !== status) {
                dispatch({
                    type: "DELETE_TICKET", payload: data.ticket.id, status: status,
                    sortDir: sortTickets
                });
            }

            if (data.action === "delete") {
                dispatch({
                    type: "DELETE_TICKET", payload: data?.ticketId, status: status,
                    sortDir: sortTickets
                });

            }
        };

        const onCompanyAppMessageTicketsList = (data) => {
            if (data.action !== "create" || !data.ticket) {
                return;
            }

            if (shouldUpdateTicket(data.ticket) && data.ticket.status === status) {
                dispatch({
                    type: "UPDATE_TICKET_UNREAD_MESSAGES",
                    payload: data.ticket,
                    status: status,
                    sortDir: sortTickets
                });
            }

            // El socket puede traer una versión parcial del ticket. Se consulta la
            // lista activa para que "Esperando" refleje el mensaje sin cambiar de pestaña.
            setLocalForceRefresh(prev => prev + 1);
            // else if (data.action === "create" && shouldUpdateTicketUser(data.ticket) && data.ticket.status === status) {
            //     dispatch({
            //         type: "UPDATE_TICKET_UNREAD_MESSAGES",
            //         payload: data.ticket,
            //     });
            // }
        };

        const onCompanyContactTicketsList = (data) => {
            if (data.action === "update" && data.contact) {
                dispatch({
                    type: "UPDATE_TICKET_CONTACT",
                    payload: data.contact,
                    status: status,
                    sortDir: sortTickets
                });
            }
        };

        const onLocalTicketListSync = (event) => {
            const detail = event?.detail || {};
            const ticketId = Number(detail.ticketId);
            const nextStatus = detail.status;

            if (!ticketId) {
                dispatch({ type: "RESET" });
                setPageNumber(1);
                setLocalForceRefresh(prev => prev + 1);
                return;
            }

            if (nextStatus && nextStatus !== status) {
                dispatch({
                    type: "DELETE_TICKET",
                    payload: ticketId,
                    status,
                    sortDir: sortTickets
                });
            }

            setLocalForceRefresh(prev => prev + 1);
        };

        const onCoachNote = data => {
            if (Number(data?.targetUserId || 0) !== Number(user?.id || 0)) {
                return;
            }

            refreshCoachPending();
        };

        const onConnectTicketsList = () => {
            if (status) {
                socket.emit("joinTickets", status);
            } else {
                socket.emit("joinNotification");
            }
        }

        socket.on("connect", onConnectTicketsList)
        socket.on(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
        socket.on(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
        socket.on(`company-${companyId}-contact`, onCompanyContactTicketsList);
        socket.on(`company-${companyId}-message-coach-note`, onCoachNote);
        window.addEventListener(TICKET_LIST_SYNC_EVENT, onLocalTicketListSync);

        return () => {
            if (status) {
                socket.emit("leaveTickets", status);
            } else {
                socket.emit("leaveNotification");
            }
            socket.off("connect", onConnectTicketsList);
            socket.off(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
            socket.off(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
            socket.off(`company-${companyId}-contact`, onCompanyContactTicketsList);
            socket.off(`company-${companyId}-message-coach-note`, onCoachNote);
            window.removeEventListener(TICKET_LIST_SYNC_EVENT, onLocalTicketListSync);
        };

    }, [status, showAll, user, selectedQueueIds, tags, users, profile, queues, sortTickets, showTicketWithoutQueue, refreshCoachPending]);

    useEffect(() => {
        if (typeof updateCount === "function") {
            updateCount(ticketsList.length);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketsList]);

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;

        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

    if (status && status !== "search") {
        ticketsList = ticketsList.filter(ticket => ticket.status === status)
    }

    return (
        <Paper className={classes.ticketsListWrapper} style={style}>
            <Paper
                square
                name="closed"
                elevation={0}
                className={classes.ticketsList}
                onScroll={handleScroll}
            >
                <List style={{ paddingTop: 0 }} >
                    {ticketsList.length === 0 && !loading ? (
                        <div className={classes.noTicketsDiv}>
                            <span className={classes.noTicketsTitle}>
                                {i18n.t("ticketsList.noTicketsTitle")}
                            </span>
                            <p className={classes.noTicketsText}>
                                {i18n.t("ticketsList.noTicketsMessage")}
                            </p>
                        </div>
                    ) : (
                        <>
                            {ticketsList.map((ticket) => {
                                const coachInfo = coachPendingByTicket[ticket.id] || coachPendingByTicket[ticket.uuid] || { count: 0, latestNote: null };

                                return (
                                    <TicketListItem
                                        ticket={ticket}
                                        key={ticket.id}
                                        setTabOpen={setTabOpen}
                                        coachPendingCount={coachInfo.count || 0}
                                        coachPendingNote={coachInfo.latestNote || null}
                                    />
                                );
                            })}
                        </>
                    )}
                    {loading && <TicketsListSkeleton />}
                </List>
            </Paper>
        </Paper>
    );
};

export default TicketsListCustom;
